<template>    
    <page-layout>
        <has-app 
            :app="$route.params.app"
            :universe="$route.params.universe"
        >
            <infinity-page
                icon="$vuetify.icons.app"
                color="teal"
                :loading="loading"
            >
                <app-drawer />
                <template #title>
                    <v-toolbar 
                        flat
                        prominent
                        class="pa-0"
                    >
                        <div>
                            <h1
                                style="font-weight: 300; font-family: lato; font-weight: bold; letter-spacing: initial;"
                            >
                                {{ app.name }}
                            </h1>
                            <h2 
                                style="font-weight: 200"
                                v-html="app.subtitle || '&nbsp;'"
                            />                            
                        </div>                        
                        <v-spacer />
                        <build-o 
                            :universe="app.universe"
                            :app="app.name"
                            info
                            class="mt-4"
                        />
                        <app-state
                            :universe="app.universe"
                            :app="app.name"
                            info
                            class="mt-4"
                        />
                    </v-toolbar>                    
                </template>
                <v-sheet>        
                    <v-container class="py-0">                        
                        <v-card
                            flat
                            class="ma-4 pt-0 pa-2 mt-0"
                        >
                            <v-card-text>
                                <a 
                                    target="_blank"
                                    :href="appUrl"
                                >
                                    <v-text-field
                                        readonly
                                        solo
                                        label="APP URL"                                        
                                        :value="appUrl"                                        
                                    >
                                        <template #prepend-inner>                                        
                                            <h3 class="infinity-text mr-2">
                                                URL
                                            </h3>
                                        </template>
                                        <template #append>
                                            <v-btn
                                                icon
                                                :href="appUrl"
                                                target="_blank"
                                            >
                                            <v-icon dark>
                                                mdi-open-in-new
                                            </v-icon>
                                            </v-btn>
                                        </template>                                    
                                    </v-text-field>
                                </a>
                                <v-row>
                                    <v-col cols="10">
                                        <v-combobox 
                                            v-model="version"
                                            :items="versions"
                                            solo
                                        />
                                    </v-col>
                                    <v-col 
                                        cols="2"
                                        style="text-align: right"
                                    >
                                        <v-btn 
                                            color="deep-purple"
                                            class="white--text"
                                            @click="newVersion"
                                        >
                                            Set version
                                        </v-btn>
                                    </v-col>
                                </v-row>
                                <v-card 
                                    flat
                                    class="description"
                                    v-html="description"
                                />
                            </v-card-text>
                            <infinity-separator 
                                icon="mdi-text-long"
                                text="App logs"
                            />
                            <log-viewer 
                                :log="appLogs || ''"
                            />
                            <v-card-actions>
                                <v-spacer />                                
                                <v-btn 
                                    color="red"
                                    class="white--text px-6 mt-8"                        
                                    depressed                                    
                                    @click="remove"
                                >
                                    <v-icon left>
                                        mdi-delete
                                    </v-icon> Delete App
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-container>                    
                </v-sheet>
                <template #sidebar>
                    <infinity-sidebar
                        icon="$vuetify.icons.help"
                        title="docs/multiverse"
                    >
                        <h4 class="text-h4 mb-3 text--primary">
                        Settings for the platform
                        </h4>

                        <p class="text--secondary">
                            Settings for the platform can be set on this page
                        </p>

                        <v-btn
                            class="mr-0 white--text"
                            color="deep-purple"                            
                            min-width="100"
                            rounded
                        >
                            I want to know more
                        </v-btn>
                    </infinity-sidebar>
                </template>
            </infinity-page>
        </has-app>
    </page-layout>
</template>
<script>
    import PageLayout from '@/layouts/default/PageLayout'    
    import AppDrawer from '@/layouts/App/Drawer'    
    import BuildO from '@/layouts/App/widgets/BuildO'
    import AppState from '@/layouts/App/widgets/State'
    import LogViewer from '@femessage/log-viewer'
    import { mapState, mapActions } from 'pinia'
    import { useAppStore } from '@/store/app-store'
    import { marked } from 'marked'

    export default {
        components: {
            PageLayout,
            AppDrawer,
            BuildO,
            AppState,
            LogViewer,
        },
        data: () => {
            return {                
                version: '',
                tick: null,
                appLogs: '',
            }            
        },
        computed: {
            ...mapState(useAppStore, ['app', 'builds', 'loading']),
            description () {
                return marked(this.app.description || '')
            },
            versions () {
                return ['latest'].concat(this.builds.map(build => build.ID))
            },
            appUrl () {
                return `https://${this.app.name}-${this.app.universe}.${window.location.host}`
            },
        },
        async mounted () {
            await this.loadApp(this.$route.params.universe, this.$route.params.app)
            await this.loadBuilds(this.$route.params.universe, this.$route.params.app)
            this.appLogs = await this.loadAppLogs(this.$route.params.universe, this.$route.params.app)
            this.version = await this.loadVersion(this.$route.params.universe, this.$route.params.app)
            this.tick = setInterval(async () => {
                this.appLogs = await this.loadAppLogs(this.$route.params.universe, this.$route.params.app)
            }, 3000)
        },
        unmounted () {
            clearInterval(this.tick)
        },
        beforeRouteLeave (to, from, next) {
            clearInterval(this.tick)
            next()
        },
        beforeDestroy () {
            clearInterval(this.tick)
        },
        deactivated () {
            clearInterval(this.tick)
        },
        methods: {
            ...mapActions(useAppStore, ['loadApp', 'deleteApp', 'loadBuilds', 'loadVersion', 'setVersion', 'startApp', 'loadAppLogs']),
            async remove () {
                await this.deleteApp(this.app)
                this.$router.push('/')
            },
            async newVersion () {
                await this.setVersion(this.$route.params.universe, this.$route.params.app, this.version)
                await this.startApp(this.$route.params.universe, this.$route.params.app)
            },
        },
    }
</script>
<style scoped>
.vjsf >>> .v-card__title {
    font-size: 24px ! important;
    font-weight: 600;
    font-family: 'lato' ! important;
}
a {
    text-decoration: none;
}
a >>> input {
    cursor: pointer;
}
</style>
